<template>
  <div :class="['container-paymentdetailcard benefit-card', { open: isCardOpen }]">
    <div class="bf-head">
      <div class="bf-tit tit-h5">
        <i18n-t keypath="view_my_subscription_benefits" scope="global">
          <template #plan>
            <span class="primary">{{ subscriptionStore.userPlan }}</span>
          </template>
        </i18n-t>
      </div>

      <button type="button" class="benefit-view-btn" @click="toggleCard">
        <img src="/icons/plus-white.svg" alt="" />
      </button>
    </div>

    <div class="character-pst">
      <img src="/images/3d-character.png" alt="" />
    </div>

    <div class="hidden-wrap">
      <div class="tit-h4 mb-6">{{ t('self_service_type_plan_feature', { plan: subscriptionStore.userPlan }) }}</div>
      <ul class="hidden-list">
        <li>
          <p>{{ t('ss_credits') }}</p>
          <ul class="my-bf-info">
            <li>
              <IconCheck />
              <span>{{ formattedBenefits.price }}</span>
            </li>
            <li>
              <IconCheck />
              <span>{{ getCreditDiscountLabel(subscriptionStore.currentPlan) }}</span>
            </li>
          </ul>
        </li>

        <li>
          <p>{{ t('language_support') }}</p>
          <ul class="my-bf-info">
            <li>
              <IconCheck />
              <span>{{ formattedBenefits.language }}</span>
            </li>
          </ul>
        </li>

        <li v-if="formattedBenefits.pricePerChat.length > 0">
          <p>{{ t('label_interpretation') }}</p>
          <ul class="my-bf-info">
            <li v-for="item in formattedBenefits.pricePerChat" :key="item">
              <IconCheck />
              <span>{{ item }}</span>
            </li>
          </ul>
        </li>

        <li v-if="formattedBenefits.features.length > 0">
          <p>{{ t('additional_features') }}</p>
          <ul class="my-bf-info">
            <li v-for="item in formattedBenefits.features" :key="item">
              <IconCheck />
              <span>{{ item }}</span>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconCheck from '@/components/jh-app/icons/IconCheck.vue';
import { usePlan } from '@/composables/shared/usePlan';
import { useSubscriptionStore } from '@/stores/subscription';

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();

const { formatBenefitByCategory, getCreditDiscountLabel } = usePlan();

const isCardOpen = ref<boolean>(false);

const formattedBenefits = computed(() => {
  return formatBenefitByCategory(
    subscriptionStore.currentPlan,
    subscriptionStore.planPrices[subscriptionStore.currentPlan]
  );
});

const toggleCard = () => {
  isCardOpen.value = !isCardOpen.value;
};
</script>

<style scoped lang="scss">
.benefit-card {
  position: relative;
  background: $gray10;
  border: 1px solid transparent !important;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    z-index: 3;
    background: rgba(0, 0, 0, 0.5);
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    backdrop-filter: blur(10px);
    opacity: 0;
    transition: all 0.2s;
  }
  .bf-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .benefit-view-btn {
    background: $color-gray-500;
    min-width: 3rem;
    max-width: 3rem;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    z-index: 5;
    cursor: pointer;
    img {
      width: 20px;
      height: 20px;
      object-fit: contain;
      transition: all 0.3s ease;
    }
  }
  .character-pst {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 640px;

    img {
      display: block;
      width: 100%;
      object-fit: contain;
    }
  }

  .hidden-wrap {
    position: absolute;
    top: 40px;
    opacity: 0;
    z-index: 4;
    color: #fff;
    width: calc(100% - 80px);
    .hidden-list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, auto);
      grid-auto-flow: column;
      row-gap: 24px;
      column-gap: 40px;
      li {
        p {
          font-weight: 600;
        }
        .my-bf-info {
          li {
            padding: $size-xs 0;
            display: flex;
            align-items: center;
            gap: $size-xs;
            span {
              font-size: $font-size-psmall;
            }
          }
        }
      }
    }
    p {
      color: #fff;
    }
  }

  &.open {
    &::before {
      opacity: 1;
    }
    .benefit-view-btn {
      img {
        transform: rotate(135deg);
      }
    }
    .hidden-wrap {
      opacity: 1;
    }
  }
}
.feature-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

@include respond-to(tablet) {
  .benefit-card {
    .hidden-wrap {
      top: 32px;
    }
  }
}
@include respond-to(mobile-landscape) {
  .benefit-card {
    .benefit-view-btn {
      min-width: 2.5rem;
      max-width: 2.5rem;
    }
    .bf-head {
      align-items: center;
    }
    .hidden-wrap {
      .tit-h4 {
        font-size: 1.25rem;
      }
    }
  }
}
@include respond-to(mobile-portrait) {
  .benefit-card {
    .hidden-wrap {
      .hidden-list {
        padding-top: 1rem;
        grid-template-columns: repeat(1, 1fr);
        grid-template-rows: auto;
        overflow-x: auto;
        width: calc(100vw - 80px);
        &::-webkit-scrollbar {
          display: none;
        }
        li {
          p,
          span {
            white-space: nowrap;
          }
        }
      }
    }
  }
}
</style>
